import { Logtail } from '@logtail/browser';

// Sensitive fields to redact from logs
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'accessToken',
  'refreshToken',
  'secret',
  'apiKey',
  'authorization',
  'bearer',
  'credentials',
  'cookie',
  'jwt',
];

/**
 * Recursively redact sensitive fields from an object
 */
const redactSensitiveData = (obj: unknown): unknown => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => redactSensitiveData(item));
  }

  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (SENSITIVE_FIELDS.some((field) => key.toLowerCase().includes(field.toLowerCase()))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = redactSensitiveData(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};

// Initialize Logtail instance
const sourceToken = import.meta.env.VITE_BETTERSTACK_SOURCE_TOKEN;
const endpoint = import.meta.env.VITE_BETTERSTACK_ENDPOINT || 'https://s1629673.eu-nbg-2.betterstackdata.com';
let logtail: Logtail | null = null;

if (sourceToken) {
  logtail = new Logtail(sourceToken, { endpoint });
} else {
  console.warn('[Logger] VITE_BETTERSTACK_SOURCE_TOKEN not set, BetterStack logging disabled');
}

/**
 * Logger interface for consistent logging throughout the app
 */
export const logger = {
  warn: (message: string, context?: Record<string, unknown>) => {
    const sanitizedContext = context ? redactSensitiveData(context) : undefined;
    console.warn(message, sanitizedContext);
    logtail?.warn(message, sanitizedContext as Record<string, unknown>);
  },

  error: (message: string, error?: Error | unknown, context?: Record<string, unknown>) => {
    const sanitizedContext = context ? redactSensitiveData(context) : undefined;
    const errorInfo =
      error instanceof Error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : { error };

    console.error(message, errorInfo, sanitizedContext);
    logtail?.error(message, {
      ...errorInfo,
      ...(sanitizedContext as Record<string, unknown>),
    });
  },

  /**
   * Flush logs immediately (useful before page unload)
   */
  flush: async () => {
    await logtail?.flush();
  },
};

export type Logger = typeof logger;
